import { Link } from "react-router-dom";
import { Truck, Award, Car, CheckCircle2, ArrowRight, DollarSign, ShieldCheck, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import DisclosureBanner from "../components/DisclosureBanner";
import JobShare from "../components/JobShare";
import { AVP_PAY, AVP_PAY_NOTE } from "../lib/avpShifts";

const POSITIONS = [
  {
    key: "full_time",
    Icon: Truck,
    tag: "Full-Time",
    title: "Full-Time Delivery Driver",
    hours: "40+ hrs / week",
    pay: "Competitive weekly pay",
    highlights: [
      "Set weekly routes and schedules",
      "Paid training and safety certification",
      "Growth path into lead & operations roles",
      "Company-provided vehicle and equipment",
    ],
    qualifications: [
      "21+ years of age",
      "Valid US driver's license, clean record",
      "Ability to lift up to 75 lbs",
      "Must pass a background check, DOT physical, and drug test",
    ],
  },
  {
    key: "experienced_fedex",
    Icon: Award,
    tag: "Fast Track Hire",
    title: "Experienced FedEx Driver",
    hours: "Flexible full-time",
    pay: "Premium pay + sign-on bonus eligible",
    highlights: [
      "Fast-track hiring for FedEx Ground / HD veterans",
      "Priority assignment to top-performing routes",
      "Weekly performance and safety incentives",
      "Refer-a-driver bonus program",
    ],
    qualifications: [
      "Prior FedEx Ground or Home Delivery experience",
      "Verifiable safety record",
      "Familiar with scanning / route apps",
      "21+ with valid license and clean MVR",
      "Must pass a background check, DOT physical, and drug test",
    ],
  },
  {
    key: "avp_priority_am",
    Icon: Car,
    tag: "AVP · 9 AM – 1 PM",
    title: "Part-Time Priority Delivery | 9:00 AM – 1:00 PM (AVP)",
    hours: "Morning shift · 9:00 AM – 1:00 PM",
    pay: AVP_PAY,
    payNote: AVP_PAY_NOTE,
    highlights: [
      "Report to the FedEx station by 9:00 AM — our goal is to have drivers dispatched by approximately 9:30 AM",
      "Focus on priority packages with 10:30 AM and 12:00 PM delivery commitments",
      "On-time delivery is a critical part of this position — consistent on-time service keeps you eligible for AVP assignments",
      "Use your own qualifying personal vehicle (sedan, SUV, minivan or covered pickup)",
      "Vehicle documents reviewed through our secure AVP portal",
    ],
    qualifications: ["21+ with a valid license (2+ years)", "Qualifying vehicle with current registration and insurance", "Must pass MVR review, background check, and any required testing"],
    href: "/apply/avp?shift=am_9_1",
    learnMore: "/avp?shift=am_9_1",
    cta: "Apply · 9 AM – 1 PM",
  },
  {
    key: "avp_priority_pm",
    Icon: Car,
    tag: "AVP · 4 PM – 8 PM",
    title: "Part-Time Priority Delivery | 4:00 PM – 8:00 PM (AVP)",
    hours: "Evening shift · 4:00 PM – 8:00 PM",
    pay: AVP_PAY,
    payNote: AVP_PAY_NOTE,
    highlights: ["Evening priority deliveries", "Consistent on-time service keeps you eligible for AVP assignments", "Use your own approved sedan, SUV, minivan or covered pickup", "Vehicle documents reviewed through our secure AVP portal"],
    qualifications: ["21+ with a valid license (2+ years)", "Qualifying vehicle with current registration and insurance", "Must pass MVR review, background check, and any required testing"],
    href: "/apply/avp?shift=pm_4_8",
    learnMore: "/avp?shift=pm_4_8",
    cta: "Apply · 4 PM – 8 PM",
  },
];

export default function Careers() {
  return (
    <div>
      <DisclosureBanner />
      <section className="py-14 md:py-20 border-b border-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Badge className="bg-amber-500/10 text-amber-400 border border-amber-500/30 uppercase tracking-widest text-[10px] px-3 py-1 rounded-full font-semibold">Careers · Baltimore, MD</Badge>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] text-white display">
              Drive a real job.<br />Build a real career.
            </h1>
            <p className="mt-5 text-slate-300 max-w-2xl leading-relaxed">
              We hire experienced delivery professionals and top-tier gig drivers who take safety, reliability, and customer service seriously. Choose the schedule that fits your life.
            </p>
            <div data-testid="hiring-requirements" className="mt-8 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6">
              <div className="text-xs uppercase tracking-[0.25em] text-amber-400 font-semibold flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Hiring Requirements — All Positions</div>
              <ul className="mt-4 grid sm:grid-cols-3 gap-3">
                {[
                  { t: "Background Check", d: "Criminal background screening" },
                  { t: "DOT Physical", d: "Current DOT medical examination" },
                  { t: "Drug Test", d: "Pre-employment drug screening" },
                ].map((r) => (
                  <li key={r.t} className="rounded-lg border border-slate-800 bg-slate-950/60 p-4">
                    <div className="flex items-center gap-2 text-white font-bold display uppercase tracking-tight"><CheckCircle2 className="w-4 h-4 text-amber-400" /> {r.t}</div>
                    <div className="mt-1 text-xs text-slate-400">{r.d}</div>
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-slate-500">Screenings are initiated only after a conditional offer of employment. Integrity Delivered, Inc. is an Equal Opportunity Employer.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 xl:grid-cols-4 gap-6">
          {POSITIONS.map((p, i) => (
            <div key={p.key} data-testid={`career-card-${p.key}`} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-7 flex flex-col">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-400 font-bold">
                <p.Icon className="w-3.5 h-3.5" /> {p.tag}
              </div>
              <h3 className="mt-3 text-2xl font-bold uppercase text-white display leading-tight">{p.title}</h3>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                <span className="inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-950 px-2.5 py-1 text-slate-300"><Calendar className="w-3 h-3" /> {p.hours}</span>
                <span className="inline-flex items-center gap-1 rounded-full border border-slate-700 bg-slate-950 px-2.5 py-1 text-slate-300"><DollarSign className="w-3 h-3" /> {p.pay}</span>
              </div>
              {p.payNote && (
                <p data-testid={`career-pay-note-${p.key}`} className="mt-3 rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 text-sm text-slate-300 leading-relaxed">{p.payNote}</p>
              )}
              <div className="mt-5 text-xs uppercase tracking-widest text-slate-500 font-semibold">Highlights</div>
              <ul className="mt-2 space-y-1.5">
                {p.highlights.map((h) => (
                  <li key={h} className="flex gap-2 text-sm text-slate-300"><CheckCircle2 className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" /> {h}</li>
                ))}
              </ul>
              <div className="mt-5 text-xs uppercase tracking-widest text-slate-500 font-semibold">Qualifications</div>
              <ul className="mt-2 space-y-1.5">
                {p.qualifications.map((q) => (
                  <li key={q} className="flex gap-2 text-sm text-slate-400"><ShieldCheck className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" /> {q}</li>
                ))}
              </ul>
              <div className="mt-auto pt-6">
                <Link to={p.href || `/apply?position=${p.key}`}>
                  <Button data-testid={`career-apply-btn-${p.key}`} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold uppercase tracking-wider">
                    {p.cta || `Apply for ${p.tag.split(" ")[0]}`} <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                {p.learnMore && (
                  <Link to={p.learnMore}>
                    <Button data-testid={`career-learn-more-btn-${p.key}`} variant="outline" className="mt-2 w-full border-amber-500/40 text-amber-300 hover:bg-amber-500/10 uppercase tracking-wider">Learn More</Button>
                  </Link>
                )}
                <JobShare jobKey={p.key} title={p.title} />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
